/**
 * Fetch wrapper that silently refreshes expired Clerk sessions on tRPC 401s.
 *
 * When the middleware returns 401 (session expired), this wrapper:
 * 1. Attempts to refresh the Clerk session via window.Clerk.session.touch()
 * 2. Retries the original request once
 * 3. If the retry succeeds → silent recovery, user never sees anything
 * 4. If the retry also fails with 401 → throws "Session expired" so
 *    sessionExpiredLink can fire analytics and redirect to sign-in
 *
 * Visitors who were NEVER signed in (anonymous public-share viewers) are
 * exempt: a 401 for them means "this procedure needs auth", not "your
 * session expired" — the response is returned untouched so the individual
 * query errors quietly instead of redirecting the visitor to /sign-in.
 */

import {
  _resetClerkLoadedWaiterForTesting,
  hasSignedInClerkClient,
  readClerk,
  waitForClerkLoaded,
} from "@/lib/clerk/read-clerk";
import { SESSION_EXPIRED_MESSAGE } from "@/lib/trpc/sessionExpiredMessage";

let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const clerk = readClerk();
      if (!clerk?.session?.touch) return false;
      await clerk.session.touch();
      return true;
    } catch {
      /* expected: Clerk session refresh may fail; caller has retry logic */
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/** Reset module state — test-only. */
export function _resetForTesting() {
  refreshPromise = null;
  _resetClerkLoadedWaiterForTesting();
}

export function createAuthFetch(baseFetch: typeof fetch = fetch): typeof fetch {
  return async function authFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const response = await baseFetch(input, init);

    if (response.status !== 401) return response;
    if (typeof window === "undefined") return response;

    const raw =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    try {
      const { pathname } = new URL(raw, window.location.origin);
      if (!pathname.startsWith("/api/trpc")) return response;
    } catch {
      /* expected: URL parsing failure for non-standard request types */
      return response;
    }

    // Anonymous visitor (e.g. public-share viewer): there is no session to
    // refresh or expire — hand the 401 back so the query errors quietly.
    if (!hasSignedInClerkClient()) {
      return response;
    }

    // No session yet but the `__client_uat` cookie hints signed-in — Clerk is
    // still loading. The cookie is only a loading-phase hint; the loaded Clerk
    // client is authoritative. Wait for it, then re-decide through
    // `hasSignedInClerkClient` so the anonymous-surface rule applies.
    if (!readClerk()?.session) {
      const loaded = await waitForClerkLoaded();
      // loaded && not signed in → the 401 is genuine; otherwise attempt refresh
      // (which throws when no session can be touched).
      if (loaded && !hasSignedInClerkClient()) {
        return response;
      }
    }

    const refreshed = await tryRefreshSession();
    if (!refreshed) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }

    const retry = await baseFetch(input, init);
    if (retry.status === 401) {
      throw new Error(SESSION_EXPIRED_MESSAGE);
    }

    return retry;
  };
}
